import {
  chooseErrorMessage,
  getErrorMessage,
  serializeErrorForLog,
} from 'shared/utils/errorHandling';
import type { ChannelBus } from './channelBus';
import { isCancelledRequest } from './cancellation';
import {
  ChannelErrorPayload,
  ChannelRequestMessage,
  isChannelRequestMessage,
  RequestMessageKind,
} from './requestMessages';
import type {
  ContractEventMeta,
  EventType,
  RequestMethod,
  RequestPayload,
  ResponsePayload,
  ChannelContract,
  ChannelContractShape,
  ContractEventOfType,
} from './contract';
import type { ChannelUnsubscribe } from './types';

export type RequestHandlers<TShape extends ChannelContractShape> = {
  [TMethod in RequestMethod<TShape>]?: (
    data: RequestPayload<TShape, TMethod>
  ) => ResponsePayload<TShape, TMethod> | Promise<ResponsePayload<TShape, TMethod>>;
};

export interface ChannelServer {
  /** Answers the contract's requests with `handlers`; acknowledges, responds and reports errors. */
  handle<TShape extends ChannelContractShape>(
    contract: ChannelContract<TShape>,
    handlers: RequestHandlers<TShape>
  ): ChannelUnsubscribe;

  /** Broadcasts an event of the contract to every context that listens to it (other tabs included). */
  publish<TShape extends ChannelContractShape, TType extends EventType<TShape>>(
    contract: ChannelContract<TShape>,
    type: TType,
    data: ContractEventOfType<TShape, TType>['data'],
    meta?: ContractEventMeta
  ): void;
}

export interface ChannelServerOptions {
  bus: ChannelBus;
  /**
   * Resolves once the handlers may run (the service worker repairs its data
   * at start). A request is acknowledged at once and answered after it, so
   * no request sent while the context starts is lost.
   */
  waitUntilServingReady?: () => Promise<unknown>;
  /**
   * Called for every failing handler, after the error was sent to the
   * requester. A cancelled handler (aborted, or its data replaced) did not
   * fail: the requester gets its error, but it is not reported.
   */
  onHandlerError?: (method: string, error: unknown, requestId: string) => void;
}

export function toChannelErrorPayload(error: unknown): ChannelErrorPayload {
  return {
    message: getErrorMessage(error) || 'Unknown error',
    messageId: chooseErrorMessage(error),
    // Plain data: a raw error may hold values a channel cannot clone.
    cause: serializeErrorForLog(error),
  };
}

/** Request handling of the service worker (or of the page, for page-answered contracts). */
export function createChannelServer({
  bus,
  waitUntilServingReady = (): Promise<void> => Promise.resolve(),
  onHandlerError = (): void => undefined,
}: ChannelServerOptions): ChannelServer {
  return {
    handle(contract, handlers) {
      const handlerByMethod = handlers as Record<string, ((data: unknown) => unknown) | undefined>;

      const answer = async ({ method, requestId, data }: ChannelRequestMessage): Promise<void> => {
        const handler = handlerByMethod[method];
        if (!handler) {
          return;
        }
        bus.post(contract, { kind: RequestMessageKind.acknowledgement, requestId });
        try {
          await waitUntilServingReady();
          const response = await handler(data);
          bus.post(contract, { kind: RequestMessageKind.response, requestId, data: response });
        } catch (error) {
          bus.post(contract, {
            kind: RequestMessageKind.response,
            requestId,
            error: toChannelErrorPayload(error),
          });
          if (!isCancelledRequest(error)) {
            onHandlerError(method, error, requestId);
          }
        }
      };

      return bus.subscribe(contract, (message) => {
        if (isChannelRequestMessage(message)) {
          void answer(message);
        }
      });
    },

    publish(contract, type, data, meta) {
      // An event without data carries no `data` key: listeners read its presence as a payload.
      bus.post(contract, data === undefined ? { ...meta, type } : { ...meta, type, data });
    },
  };
}
